import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FileViewerImageComponent } from '../file-viewer-image/file-viewer-image.component';
import { FileViewerPdfComponent } from '../file-viewer-pdf/file-viewer-pdf.component';
import { FileViewerVideoComponent } from '../file-viewer-video/file-viewer-video.component';
import { DialogFileKind, FileViewerDialogData } from '../../types/file-viewer.types';
import { combineLatest, Subscription } from 'rxjs';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { getFileViewerTranslations } from '../../constants/file-viewer.translations';
import { resolveLanguageCode, safeDecodeURIComponent } from '../../utils/file-viewer.utils';
import { BrowserModule } from "@angular/platform-browser";

@Component({
  selector: 'app-file-viewer-page',
  imports: [FileViewerVideoComponent, FileViewerPdfComponent, FileViewerImageComponent, BrowserModule],
  templateUrl: './file-viewer-page.component.html',
  styleUrl: './file-viewer-page.component.scss',
})
export class FileViewerPageComponent implements OnInit, OnDestroy {
  data: FileViewerDialogData | null = null;

  @ViewChild(FileViewerImageComponent) imageViewer?: FileViewerImageComponent;
  @ViewChild(FileViewerPdfComponent) pdfViewer?: FileViewerPdfComponent;
  @ViewChild(FileViewerVideoComponent) videoViewer?: FileViewerVideoComponent;

  private readonly routeSubscription = new Subscription();

  constructor(private route: ActivatedRoute) {}

  get translations() {
    return getFileViewerTranslations(this.data?.languageCode || 'it');
  }

  get isImage(): boolean {
    return this.data?.kind === 'image';
  }

  get isPdf(): boolean {
    return this.data?.kind === 'pdf';
  }

  get isVideo(): boolean {
    return this.data?.kind === 'video';
  }

  ngOnInit(): void {
    this.routeSubscription.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
        ([paramMap, queryParamMap]) => {
          this._syncViewerData(paramMap, queryParamMap.get('lang'));
        },
      ),
    );
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  goBack(): void {
    window.history.back();
  }

  zoomIn(): void {
    this.imageViewer?.zoomIn();
  }

  zoomOut(): void {
    this.imageViewer?.zoomOut();
  }

  fitToWidth(): void {
    this.imageViewer?.fitToWidth();
  }

  fitToScreen(): void {
    this.imageViewer?.fitToScreen();
  }

  rotate(): void {
    this.imageViewer?.rotate();
  }

  openInNewTab(): void {
    this._runCurrentViewerAction({
      image: () => this.imageViewer?.openInNewTab(),
      pdf: () => this.pdfViewer?.openInNewTab(),
      video: () => this.videoViewer?.openInNewTab(),
    });
  }

  download(): void {
    this._runCurrentViewerAction({
      image: () => this.imageViewer?.download(),
      pdf: () => this.pdfViewer?.download(),
      video: () => this.videoViewer?.download(),
    });
  }

  private _syncViewerData(paramMap: ParamMap, languageCode: string | null): void {
    const kind = this._resolveViewerKind(paramMap.get('type'));
    const rawSrc = paramMap.get('src');

    if (!kind || !rawSrc) {
      this.data = null;
      return;
    }

    const src = safeDecodeURIComponent(rawSrc);
    const decodedName = safeDecodeURIComponent(paramMap.get('name') || '');

    this.data = {
      kind,
      name: decodedName || src,
      src,
      languageCode: resolveLanguageCode(languageCode),
    };
  }

  private _resolveViewerKind(type: string | null): DialogFileKind | null {
    if (type === 'image' || type === 'pdf' || type === 'video') {
      return type;
    }

    return null;
  }

  private _runCurrentViewerAction(actions: {
    image: () => void;
    pdf: () => void;
    video: () => void;
  }): void {
    if (this.isImage) {
      actions.image();
      return;
    }

    if (this.isPdf) {
      actions.pdf();
      return;
    }

    if (this.isVideo) {
      actions.video();
    }
  }
}
